import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Key, Plus, CheckCircle2, Clock, XCircle, Lock } from 'lucide-react';

import API from '../../constants/api'
// API base via getApiBase()

const moduleOptions = [
  { value: 'artisan_profile_access', label: 'Fiche artisan', description: 'Créer et gérer votre profil professionnel' },
  { value: 'artisan_directory_visibility', label: 'Visibilité annuaire', description: 'Apparaître dans l\'annuaire des artisans' },
  { value: 'request_response_access', label: 'Répondre aux demandes', description: 'Proposer vos services aux clients' },
  { value: 'job_offer_publisher', label: 'Publier des offres', description: 'Publier des offres d\'emploi' },
  { value: 'job_application_management', label: 'Gérer candidatures', description: 'Gérer les candidatures reçues' },
  { value: 'kyc_submission_access', label: 'Soumission KYC', description: 'Soumettre votre vérification d\'identité' }
];

const statusInfo = {
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approuvee: { label: 'Approuvée', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  refusee: { label: 'Refusée', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function AccessRequests() {
  const { user, token, hasModule, refreshUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    module_name: '',
    message: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/feature-requests/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/feature-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande envoyée avec succès');
      setDialogOpen(false);
      setFormData({ module_name: '', message: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const availableModules = moduleOptions.filter(
    m => !hasModule(m.value) && !requests.some(r => r.module_name === m.value && r.status === 'en_attente')
  );

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Demandes d'accès</h1>
            <p className="text-slate-600">Demandez l'accès à de nouvelles fonctionnalités</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button disabled={availableModules.length === 0} data-testid="new-request-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle demande
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Demander un accès</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="access-request-form">
                <div>
                  <label className="form-label">Fonctionnalité *</label>
                  <Select value={formData.module_name} onValueChange={(v) => setFormData({ ...formData, module_name: v })}>
                    <SelectTrigger data-testid="module-select">
                      <SelectValue placeholder="Sélectionner une fonctionnalité" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModules.map((mod) => (
                        <SelectItem key={mod.value} value={mod.value}>
                          <div>
                            <div className="font-medium">{mod.label}</div>
                            <div className="text-xs text-slate-500">{mod.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="form-label">Message (optionnel)</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Expliquez pourquoi vous souhaitez cet accès..."
                    rows={3}
                    data-testid="request-message"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={!formData.module_name || submitting} data-testid="submit-request">
                    {submitting ? 'Envoi...' : 'Envoyer la demande'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Available Modules */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Modules disponibles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {moduleOptions.map((mod) => {
              const isActive = hasModule(mod.value);
              const isPending = requests.some(r => r.module_name === mod.value && r.status === 'en_attente');
              
              return (
                <div
                  key={mod.value}
                  className={`rounded-xl border p-4 shadow-soft transition-all duration-300 ease-lux ${
                    isActive ? 'bg-green-50 border-green-200' :
                    isPending ? 'bg-yellow-50 border-yellow-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-slate-900">{mod.label}</h3>
                      <p className="text-sm text-slate-600">{mod.description}</p>
                    </div>
                    {isActive ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : isPending ? (
                      <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    ) : (
                      <Lock className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="mt-2">
                    {isActive ? (
                      <Badge className="badge-new">Actif</Badge>
                    ) : isPending ? (
                      <Badge className="badge-pending">En attente</Badge>
                    ) : (
                      <Badge className="badge-locked">Verrouillé</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Request History */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Historique des demandes</h2>
          
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="lux-dashboard-card p-4 animate-pulse">
                  <div className="h-5 bg-slate-200 rounded w-1/4 mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="lux-dashboard-card p-8 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-600">Aucune demande effectuée</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((req) => {
                const StatusIcon = statusInfo[req.status]?.icon || Clock;
                const modInfo = moduleOptions.find(m => m.value === req.module_name);
                
                return (
                  <div key={req.id} className="lux-dashboard-card p-4" data-testid={`request-${req.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-slate-900">{modInfo?.label || req.module_name}</h3>
                        {req.message && <p className="text-sm text-slate-600 mt-1">{req.message}</p>}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(req.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Badge className={statusInfo[req.status]?.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusInfo[req.status]?.label}
                      </Badge>
                    </div>
                    {req.admin_comment && (
                      <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">
                        <strong>Réponse :</strong> {req.admin_comment}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
